import { ProjectFinancialsContractType } from 'api/project';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  PlannerGridData,
  Step,
  StepsData,
  StepsRowData,
  StrategyOptions,
} from './types';

export const getNextIndex = (steps: Step[], selectedStep: Step) => {
  const index = steps.findIndex((item) => item.id === selectedStep.id);
  const nextIndex = (index + 1) % steps.length === 0 ? 0 : index + 1;

  return nextIndex;
};

export const getPreviousIndex = (steps: Step[], selectedStep: Step) => {
  const currentIndex = steps.findIndex((item) => item.id === selectedStep.id);

  return currentIndex - 1;
};

export const isMaximumMarginDisabled = ({
  strategy,
  financialContractType,
  isRateCardEnabled,
  hasAccessToMaximizeMargin,
}: {
  strategy;
  financialContractType: ProjectFinancialsContractType;
  isRateCardEnabled: boolean;
  hasAccessToMaximizeMargin: boolean;
}) => {
  if (strategy.value === StrategyOptions.MaximumMargin) {
    if (financialContractType === ProjectFinancialsContractType.SUBSCRIPTION) {
      return false;
    }
    if (
      financialContractType === ProjectFinancialsContractType.NONBILLABLE ||
      !hasAccessToMaximizeMargin
    ) {
      return true;
    }
    if (
      (financialContractType !== ProjectFinancialsContractType.FIXED &&
        !isRateCardEnabled) ||
      !hasAccessToMaximizeMargin
    ) {
      return true;
    }
  }

  return false;
};

export const getTooltipText = (
  financialContractType: ProjectFinancialsContractType,
) => {
  if (financialContractType === ProjectFinancialsContractType.NONBILLABLE) {
    return 'maximum_margin_disabled_for_non_billable_project';
  }

  return 'rate_cards_not_enabled_text';
};

export const getUpdatedRowData = (selectedRow) => {
  return {
    allocationStartDate: selectedRow.allocatedDates.startDate,
    allocationEndDate: selectedRow.allocatedDates.endDate,
    allocatedMinutes: selectedRow.allocatedMinutes,
    team: {
      teamId: selectedRow.id,
      teamName: selectedRow.teamName,
      roleId: selectedRow.roleId,
    },
    dateEditable: selectedRow.isDatesEditable,
    resolved: false,
    simpleSearchFilter: selectedRow.simpleSearchFilter,
    isNoMatchFoundShown: false,
  };
};

export const updateRowDataWithNoMatchFoundStatus = (
  stepsData: StepsData,
): StepsData => {
  const updatedRowData = stepsData.rows.map((item) => ({
    ...item,
    isNoMatchFoundShown: !!(
      item.allocationStartDate &&
      item.allocationEndDate &&
      item.allocatedMinutes &&
      !item.user?.userId
    ),
  }));

  return { ...stepsData, rows: updatedRowData };
};

export const getMarginColor = (margin: number) => {
  if (margin <= 0) {
    return 'var(--red-60)';
  }

  return 'var(--green-50)';
};

export const getUtilizationColor = (utilization: number) => {
  if (utilization <= 100) {
    return 'var(--green-50)';
  }

  return 'var(--red-60)';
};

export const removeTeamMembersIfNoAccess = ({
  fields,
  peopleIHaveAccess,
}: {
  fields: FilterableField[];
  peopleIHaveAccess: number[];
}) => {
  return fields?.map((field: any) =>
    field?.fieldName === 'teamMembers'
      ? {
          ...field,
          metaData: {
            ...field?.metaData,
            users: {
              ...field?.metaData?.users,
              active_user_ids: field?.metaData?.users?.active_user_ids?.filter(
                (x) => peopleIHaveAccess?.includes(x),
              ),
              non_external_user_ids:
                field?.metaData?.users?.non_external_user_ids?.filter((x) =>
                  peopleIHaveAccess?.includes(x),
                ),
            },
          },
        }
      : field,
  );
};

export const checkIfUserWithZeroHoursAssignedToTeam = (
  row: StepsRowData,
  rows?: StepsRowData[],
) => {
  if (
    row.team == null &&
    row.user != null &&
    (row.allocatedMinutes === 0 || row.allocatedMinutes == null)
  ) {
    const isAnotherVariantOfUserExists = !!rows?.find(
      (x) => x.team != null && x.user?.userId === row.user?.userId,
    );

    return isAnotherVariantOfUserExists;
  }

  return false;
};
export const getUserIdFromTransformedRow = <T extends PlannerGridData>(
  row: T,
): number | undefined | null => row.teamMember;
const pickEarlyStartDate = (
  date1?: string,
  date2?: string,
): string | undefined => {
  if (date1 == null || date2 == null) {
    return date1 ?? date2;
  }

  return new Date(date1) < new Date(date2) ? date1 : date2;
};
const pickLateEndDate = (
  date1?: string,
  date2?: string,
): string | undefined => {
  if (date1 == null || date2 == null) {
    return date1 ?? date2;
  }

  return new Date(date1) > new Date(date2) ? date1 : date2;
};

type EarliestStartDateAndLatestEndDate = {
  startDate?: string;
  endDate?: string;
};
type DateGetter<T extends PlannerGridData> = (row: T) => string | undefined;
export const getEarliestStartDateAndLatestEndDateFromTransformedRows = <
  T extends PlannerGridData,
>(
  rows: T[],
  startDateGetter: DateGetter<T>,
  endDateGetter: DateGetter<T>,
): EarliestStartDateAndLatestEndDate => {
  return rows?.reduce(
    (dates: EarliestStartDateAndLatestEndDate, row) => {
      return {
        startDate: pickEarlyStartDate(startDateGetter(row), dates.startDate),
        endDate: pickLateEndDate(endDateGetter(row), dates.endDate),
      };
    },
    {
      startDate: startDateGetter(rows[0]),
      endDate: endDateGetter(rows[0]),
    },
  );
};

export const sumRowValues = <T extends PlannerGridData>(
  rows: T[],
  valueGetter: (row: T) => number | undefined,
) => rows.reduce((result, row) => result + (valueGetter(row) ?? 0), 0);
export const checkIfAnotherVariantOfUserAvailable = <T extends PlannerGridData>(
  row: T,
  rows?: StepsRowData[],
) => {
  if (row.teamMember != null) {
    return !!rows?.find(
      (x) =>
        row.teamMember === x.user?.userId &&
        (row.teamId ?? null) !== (x.team?.teamId ?? null),
    );
  }

  return false;
};
